import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Calendar, Linkedin, Twitter, Plus, ChevronLeft, ChevronRight,
  Loader2, Trash2, Send, Clock, AlertCircle, CheckCircle2, Edit2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_CONFIG = {
  linkedin: { label: 'LinkedIn', icon: Linkedin, color: 'bg-[#0A66C2]', border: 'border-[#0A66C2]', text: 'text-[#0A66C2]' },
  twitter: { label: 'X / Twitter', icon: Twitter, color: 'bg-gray-900', border: 'border-gray-900', text: 'text-gray-900' },
};

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Clock },
  posting: { label: 'Posting...', color: 'bg-amber-100 text-amber-700', icon: Loader2 },
  posted: { label: 'Posted', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    dates.push(dd);
  }
  return dates;
}

function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' }).format(d) + ' EST';
}

function formatWeekLabel(dates) {
  const start = dates[0];
  const end = dates[6];
  const fmt = (d) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }).format(d);
  return `${fmt(start)} - ${fmt(end)}, ${end.getFullYear()}`;
}

export default function SocialCalendarPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baseDate, setBaseDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    platform: 'linkedin',
    content: '',
    scheduled_at: '',
    headline: '',
    image_url: '',
  });
  const [saving, setSaving] = useState(false);

  const weekDates = getWeekDates(baseDate);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const start = weekDates[0].toISOString();
      const end = new Date(weekDates[6].getTime() + 86400000).toISOString();
      const { data } = await axios.get(`${API}/api/social/calendar/posts?start=${start}&end=${end}`, { withCredentials: true });
      setPosts(data);
    } catch (e) {
      console.error('Failed to load calendar posts', e);
    }
    setLoading(false);
  }, [baseDate]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const prevWeek = () => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() - 7);
    setBaseDate(d);
  };

  const nextWeek = () => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + 7);
    setBaseDate(d);
  };

  const thisWeek = () => setBaseDate(new Date());

  const openNewForm = (date) => {
    const dt = new Date(date);
    dt.setHours(10, 0, 0, 0);
    setFormData({
      platform: 'linkedin',
      content: '',
      scheduled_at: dt.toISOString().slice(0, 16),
      headline: '',
      image_url: '',
    });
    setEditingPost(null);
    setShowForm(true);
  };

  const openEditForm = (post) => {
    setFormData({
      platform: post.platform,
      content: post.content,
      scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '',
      headline: post.headline || '',
      image_url: post.image_url || '',
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const savePost = async () => {
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }
    if (!formData.scheduled_at) {
      toast.error('Schedule date/time is required');
      return;
    }

    setSaving(true);
    try {
      const body = {
        ...formData,
        scheduled_at: new Date(formData.scheduled_at).toISOString(),
      };

      if (editingPost) {
        await axios.put(`${API}/api/social/calendar/posts/${editingPost.id}`, body, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/api/social/calendar/posts`, body, { withCredentials: true });
        toast.success('Post scheduled');
      }
      setShowForm(false);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save post');
    }
    setSaving(false);
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API}/api/social/calendar/posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchPosts();
    } catch (e) {
      toast.error('Failed to delete post');
    }
  };

  const publishNow = async (postId) => {
    try {
      const { data } = await axios.post(`${API}/api/social/calendar/posts/${postId}/publish-now`, {}, { withCredentials: true });
      if (data.ok) {
        toast.success('Post published!');
      } else {
        toast.error(data.error || 'Failed to publish');
      }
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to publish');
    }
  };

  const getPostsForDate = (date) => {
    const dateStr = formatDate(date);
    return posts.filter(p => {
      const pDate = p.scheduled_at ? new Date(p.scheduled_at).toISOString().split('T')[0] : '';
      return pDate === dateStr;
    });
  };

  const today = formatDate(new Date());

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-purple-100 rounded-lg p-2">
            <Calendar className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Social Calendar</h1>
            <p className="text-sm text-gray-500">Schedule and manage social media posts</p>
          </div>
        </div>
        <Button onClick={() => openNewForm(new Date())} className="gap-2 bg-[#0A66C2] hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Schedule Post
        </Button>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={thisWeek}>Today</Button>
          <Button variant="outline" size="sm" onClick={nextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="text-lg font-semibold text-gray-800">{formatWeekLabel(weekDates)}</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#0A66C2]" />
            <span className="text-xs text-gray-500">LinkedIn</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gray-900" />
            <span className="text-xs text-gray-500">X / Twitter</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const isToday = dateStr === today;
          const dayPosts = getPostsForDate(date);
          const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

          return (
            <div
              key={i}
              className={`min-h-[200px] rounded-xl border ${isToday ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 bg-white'} p-2`}
            >
              {/* Day Header */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs text-gray-400">{dayNames[i]}</span>
                  <span className={`ml-1.5 text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-gray-800'}`}>
                    {date.getDate()}
                  </span>
                </div>
                <button
                  onClick={() => openNewForm(date)}
                  className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Posts */}
              <div className="space-y-1.5">
                {dayPosts.map((post) => {
                  const pc = PLATFORM_CONFIG[post.platform] || PLATFORM_CONFIG.linkedin;
                  const sc = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;
                  const PlatIcon = pc.icon;

                  return (
                    <div
                      key={post.id}
                      className={`rounded-lg border ${post.status === 'scheduled' ? pc.border : 'border-gray-200'} border-opacity-30 p-2 cursor-pointer hover:shadow-sm transition-shadow group`}
                      onClick={() => post.status === 'scheduled' ? openEditForm(post) : null}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className={`${pc.color} rounded p-0.5`}>
                          <PlatIcon className="w-2.5 h-2.5 text-white" />
                        </div>
                        <span className="text-[10px] text-gray-500">{formatTime(post.scheduled_at)}</span>
                        <Badge className={`${sc.color} text-[9px] px-1 py-0 h-4 ml-auto`}>{sc.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-700 line-clamp-3 leading-tight">{post.content}</p>

                      {/* Actions (shown on hover for scheduled posts) */}
                      {post.status === 'scheduled' && (
                        <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); publishNow(post.id); }}
                            className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                          >
                            <Send className="w-2.5 h-2.5" /> Publish
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deletePost(post.id); }}
                            className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5 ml-auto"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                      {post.status === 'failed' && post.error && (
                        <p className="text-[10px] text-red-500 mt-1 truncate">{post.error}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Schedule / Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {editingPost ? 'Edit Scheduled Post' : 'Schedule New Post'}
                </CardTitle>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Platform */}
              <div>
                <Label className="text-sm mb-2 block">Platform</Label>
                <div className="flex gap-2">
                  {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    const selected = formData.platform === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setFormData(f => ({ ...f, platform: key }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          selected ? `${cfg.border} bg-blue-50` : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`${cfg.color} rounded p-1`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm font-medium">{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div>
                <Label className="text-sm mb-1 block">Content</Label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(f => ({ ...f, content: e.target.value }))}
                  placeholder="Write your post content..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.content.length} characters</p>
              </div>

              {/* Schedule Date/Time */}
              <div>
                <Label className="text-sm mb-1 block">Schedule Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData(f => ({ ...f, scheduled_at: e.target.value }))}
                />
              </div>

              {/* Image URL (optional) */}
              <div>
                <Label className="text-sm mb-1 block">Image URL (optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button onClick={savePost} disabled={saving} className="flex-1 gap-2 bg-[#0A66C2] hover:bg-blue-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                  {editingPost ? 'Update Post' : 'Schedule Post'}
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
